import { Check as CheckIcon } from '@mui/icons-material';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  getGridBooleanOperators,
  getGridMultiSelectOperators,
  getGridSingleSelectOperators,
  isAutogeneratedRow,
  type GridColDef,
  type GridFilterOperator,
} from '@mui/x-data-grid-premium';
import { CustomFieldType, type CustomField } from '../GeneratedClient';
import { TestFamilyBadge } from '../Families/TestFamilyBadge';
import { v2Typography } from '../Families/v2Typography';
import { formatCustomFieldGridValue } from '../Generic/customFieldValue';
import { renderVolunteerCustomFieldValue } from './VolunteerApprovalTab/volunteerCustomFieldPresentation';
import { VolunteerApprovalRolesCellV2 } from './VolunteerApprovalRolesCellV2';
import {
  completeRequirementFilterValue,
  missingRequirementFilterValue,
  type VolunteerMissingRequirementGroup,
} from './VolunteerApprovalTab/volunteerMissingRequirementsPresentation';
import { notAppliedLabel } from './VolunteerApprovalTab/catchAllLabel';
import {
  optedOutRoleStatusValue,
  roleApprovalStatusFilterOptions,
} from './roleApprovalStatusPresentation';
import type {
  VolunteerBrowserRowV2,
  VolunteerCustomFieldValue,
} from './useVolunteersBrowserViewModel';

export const VOLUNTEERS_SEARCH_FIELD = 'searchableText';
const noAnalytics = { aggregable: false, chartable: false, pivotable: false };
const MAX_VISIBLE_MISSING_REQUIREMENTS = 4;
const emptyOperators = <V,>(): GridFilterOperator<
  VolunteerBrowserRowV2,
  V
>[] => [
  {
    label: 'is empty',
    value: 'isEmpty',
    requiresFilterValue: false,
    getApplyFilterFn: () => (value) =>
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0),
  },
  {
    label: 'is not empty',
    value: 'isNotEmpty',
    requiresFilterValue: false,
    getApplyFilterFn: () => (value) =>
      value !== null &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0),
  },
];
const membershipOperators = getGridMultiSelectOperators();
const statusOptions = [
  ...roleApprovalStatusFilterOptions(notAppliedLabel),
  { value: optedOutRoleStatusValue, label: 'Opted out' },
];

export function requirementFilterLabel(value: string) {
  if (value === missingRequirementFilterValue) return 'Missing';
  if (value === completeRequirementFilterValue) return 'Complete';
  return value;
}

export function requirementFilterOptions(values: string[]) {
  return Array.from(new Set(values)).map((value) => ({
    label: requirementFilterLabel(value),
    value,
  }));
}

export function formatMissingRequirements(
  groups: VolunteerMissingRequirementGroup[]
) {
  return groups.length
    ? groups
        .map((group) => `${group.label}: ${group.requirements.join(', ')}`)
        .join('; ')
    : 'Complete';
}

export function formatRequirementFilterValues(value: unknown) {
  return Array.isArray(value)
    ? value.map((item) => requirementFilterLabel(String(item))).join(', ')
    : value === null || value === undefined
      ? ''
      : requirementFilterLabel(String(value));
}

function compareText(left: unknown, right: unknown) {
  const leftText = typeof left === 'string' && left.length > 0 ? left : null;
  const rightText =
    typeof right === 'string' && right.length > 0 ? right : null;

  if (leftText === null) return rightText === null ? 0 : 1;
  if (rightText === null) return -1;
  return leftText.localeCompare(rightText);
}

function isVolunteerFamilySortRow(
  row: unknown
): row is Pick<VolunteerBrowserRowV2, 'familyLastName' | 'id'> {
  return (
    typeof row === 'object' &&
    row !== null &&
    typeof (row as { familyLastName?: unknown }).familyLastName === 'string' &&
    typeof (row as { id?: unknown }).id === 'string'
  );
}

export function compareVolunteerFamilyRows(
  first: unknown,
  second: unknown,
  firstValue: unknown,
  secondValue: unknown,
  firstId: unknown,
  secondId: unknown
) {
  if (isVolunteerFamilySortRow(first) && isVolunteerFamilySortRow(second)) {
    return (
      first.familyLastName.localeCompare(second.familyLastName) ||
      first.id.localeCompare(second.id)
    );
  }

  return compareText(firstValue, secondValue) || compareText(firstId, secondId);
}

function renderMissingRequirementsCell(row: VolunteerBrowserRowV2) {
  const missingRequirements = row.missingRequirementGroups.flatMap(
    (group) => group.requirements
  );

  if (!missingRequirements.length) {
    return (
      <Chip
        color="success"
        icon={<CheckIcon />}
        label="Complete"
        size="small"
        variant="outlined"
      />
    );
  }

  let visibleCount = 0;
  const visibleGroups = row.missingRequirementGroups.flatMap((group) => {
    if (visibleCount >= MAX_VISIBLE_MISSING_REQUIREMENTS) return [];

    const requirements = group.requirements.slice(
      0,
      MAX_VISIBLE_MISSING_REQUIREMENTS - visibleCount
    );
    visibleCount += requirements.length;
    return [{ ...group, requirements }];
  });
  const hiddenCount = missingRequirements.length - visibleCount;
  const tooltipTitle = row.missingRequirementGroups
    .map((group) => `${group.label}:\n${group.requirements.join('\n')}`)
    .join('\n');

  return (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>}
    >
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {visibleGroups.map((group) => (
          <Box key={group.label} sx={{ minWidth: 0 }}>
            <Typography noWrap {...v2Typography.fieldLabel}>
              {group.label}
            </Typography>
            {group.requirements.map((requirement) => (
              <Typography
                key={`${group.label}:${requirement}`}
                noWrap
                {...v2Typography.browserSecondary}
              >
                • {requirement}
              </Typography>
            ))}
          </Box>
        ))}
        {hiddenCount > 0 && (
          <Typography noWrap {...v2Typography.browserSecondary}>
            +{hiddenCount} more
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

export function buildVolunteerCustomColumns(
  scope: 'Volunteer' | 'Family',
  fields: CustomField[],
  rows: VolunteerBrowserRowV2[]
) {
  const values = (row: VolunteerBrowserRowV2, name: string) =>
    (scope === 'Volunteer'
      ? row.volunteerCustomFieldValues
      : row.familyCustomFieldValues)[name] ?? null;
  return fields.map((definition): GridColDef<VolunteerBrowserRowV2> => {
    const field = `${scope.toLowerCase()}CustomField:${definition.name}`;
    const observed = rows.map((row) => values(row, definition.name));
    if (definition.type === CustomFieldType.StringArray) {
      const options = Array.from(
        new Set([
          ...(definition.validValues ?? []),
          ...observed.flatMap((value) => (Array.isArray(value) ? value : [])),
        ])
      );
      return {
        ...noAnalytics,
        field,
        headerName: `${scope}: ${definition.name}`,
        type: 'multiSelect',
        minWidth: 180,
        flex: 1,
        valueOptions: options,
        valueGetter: (_value, row) => values(row, definition.name),
        getApplyQuickFilterFn: () => null,
        valueFormatter: (value) =>
          Array.isArray(value) ? Array.from(value as string[]).join(', ') : '',
        filterOperators: [
          ...membershipOperators,
          ...emptyOperators<string[] | null>(),
        ],
        renderCell: ({ value }) => (
          <Typography {...v2Typography.browserCell}>
            {renderVolunteerCustomFieldValue(value, definition.validValues) ||
              '-'}
          </Typography>
        ),
      };
    }
    if (
      definition.type === CustomFieldType.DateOnly ||
      definition.type === CustomFieldType.DateTime
    ) {
      return {
        ...noAnalytics,
        field,
        headerName: `${scope}: ${definition.name}`,
        type:
          definition.type === CustomFieldType.DateOnly ? 'date' : 'dateTime',
        minWidth: 180,
        flex: 1,
        valueGetter: (_value, row) => values(row, definition.name),
        getApplyQuickFilterFn: () => null,
        valueFormatter: (value) =>
          formatCustomFieldGridValue(definition.type!, value),
        renderCell: ({ value }) => (
          <Typography {...v2Typography.browserCell}>
            {formatCustomFieldGridValue(definition.type!, value) || '-'}
          </Typography>
        ),
      };
    }
    const categorical =
      definition.type === CustomFieldType.String &&
      (definition.validValues?.length ?? 0) > 0 &&
      (definition.validValues?.length ?? 0) <= 50 &&
      observed.every(
        (value) =>
          value === null ||
          value === '' ||
          definition.validValues!.includes(String(value))
      );
    if (categorical)
      return {
        aggregable: false,
        chartable: observed.every((value) => value !== null),
        pivotable: true,
        field,
        headerName: `${scope}: ${definition.name}`,
        type: 'singleSelect',
        minWidth: 180,
        flex: 1,
        valueOptions: definition.validValues!,
        valueGetter: (_value, row) => values(row, definition.name),
        getApplyQuickFilterFn: () => null,
        filterOperators: [
          ...getGridSingleSelectOperators(),
          ...emptyOperators<string | null>(),
        ],
        valueFormatter: (value) => value ?? '',
      };
    const boolean = definition.type === CustomFieldType.Boolean;
    return {
      aggregable: false,
      chartable: boolean && observed.every((value) => value !== null),
      pivotable: boolean,
      field,
      headerName: `${scope}: ${definition.name}`,
      type: boolean ? 'boolean' : 'string',
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) => values(row, definition.name),
      getApplyQuickFilterFn: () => null,
      filterOperators: boolean
        ? [...getGridBooleanOperators(), ...emptyOperators<boolean | null>()]
        : undefined,
      valueFormatter: (value: VolunteerCustomFieldValue) =>
        typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value ?? ''),
      renderCell: ({ value }) => (
        <Typography {...v2Typography.browserCell}>
          {renderVolunteerCustomFieldValue(value, definition.validValues) ||
            '-'}
        </Typography>
      ),
    };
  });
}

export function buildVolunteersGridColumns({
  arrangementTypes,
  familyCustomFields,
  rows,
  roleNames,
  updateTestFamilyFlagEnabled,
  volunteerCustomFields,
}: {
  arrangementTypes: string[];
  familyCustomFields: CustomField[];
  rows: VolunteerBrowserRowV2[];
  roleNames: string[];
  updateTestFamilyFlagEnabled?: boolean;
  volunteerCustomFields: CustomField[];
}): GridColDef<VolunteerBrowserRowV2>[] {
  const counties = Array.from(
    new Set(
      rows
        .map((row) => row.county)
        .filter((county): county is string => county !== null)
    )
  ).sort((first, second) => first.localeCompare(second));
  const userRoleFilterOptions = Array.from(
    new Set(rows.flatMap((row) => row.userRoles))
  ).sort((first, second) => first.localeCompare(second));
  const columns: GridColDef<VolunteerBrowserRowV2>[] = [
    {
      ...noAnalytics,
      field: 'family',
      headerName: 'Family Name',
      flex: 1,
      minWidth: 220,
      filterable: false,
      sortComparator: (firstValue, secondValue, first, second) =>
        compareVolunteerFamilyRows(
          first.api.getRow(first.id),
          second.api.getRow(second.id),
          firstValue,
          secondValue,
          first.id,
          second.id
        ),
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          value
        ) : (
          <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
            <Typography noWrap {...v2Typography.browserCell}>
              {row.family}
            </Typography>
            {updateTestFamilyFlagEnabled && (
              <TestFamilyBadge family={row.sourceFamily} />
            )}
          </Box>
        ),
    },
    {
      ...noAnalytics,
      field: 'primaryContact',
      headerName: 'Primary Contact',
      flex: 0.7,
      minWidth: 160,
      filterable: false,
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          value
        ) : (
          <Typography noWrap {...v2Typography.browserCell}>
            {row.primaryContact}
          </Typography>
        ),
    },
    {
      aggregable: false,
      chartable: rows.every((row) => row.county !== null),
      field: 'county',
      headerName: 'County',
      type: 'singleSelect',
      minWidth: 140,
      flex: 1,
      pivotable: true,
      valueOptions: counties,
      filterOperators: [
        ...getGridSingleSelectOperators(),
        ...emptyOperators<string | null>(),
      ],
      getApplyQuickFilterFn: () => null,
      valueFormatter: (value: string | null) => value ?? '',
      renderCell: ({ value }) => (
        <Typography {...v2Typography.browserCell}>{value || '-'}</Typography>
      ),
    },
    {
      ...noAnalytics,
      field: 'roles',
      headerName: 'Volunteer Roles',
      flex: 1.5,
      minWidth: 360,
      sortable: false,
      type: 'multiSelect',
      valueOptions: roleNames,
      valueGetter: (_value, row) => row.roleFilterValues,
      valueFormatter: (_value, row) => row.roleFilterValues.join(', '),
      filterOperators: [...membershipOperators, ...emptyOperators<string[]>()],
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          value
        ) : (
          <VolunteerApprovalRolesCellV2 roles={row.roles} />
        ),
    },
    {
      ...noAnalytics,
      field: 'userRoles',
      headerName: 'User Roles',
      flex: 1,
      minWidth: 180,
      sortable: false,
      type: 'multiSelect',
      valueOptions: userRoleFilterOptions,
      valueGetter: (_value, row) => row.userRoles,
      valueFormatter: (_value, row) => row.userRoles.join(', '),
      filterOperators: [...membershipOperators, ...emptyOperators<string[]>()],
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          value
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', py: 0.5 }}>
            {row.userRoles.map((role) => (
              <Chip
                key={role}
                color="info"
                label={role}
                size="small"
                sx={{ margin: '.125rem .25rem .125rem 0' }}
              />
            ))}
          </Box>
        ),
    },
    ...roleNames
      .filter((roleName) => roleName !== notAppliedLabel)
      .map(
        (roleName): GridColDef<VolunteerBrowserRowV2> => ({
          ...noAnalytics,
          field: `roleStatus:${roleName}`,
          headerName: `${roleName} Status`,
          minWidth: 180,
          sortable: false,
          type: 'multiSelect',
          valueOptions: statusOptions.filter(
            (option) => option.label !== notAppliedLabel
          ),
          valueGetter: (_value, row) => row.roleStatusValues[roleName] ?? [],
          valueFormatter: (_value, row) =>
            (row.roleStatusValues[roleName] ?? [])
              .map(
                (status) =>
                  statusOptions.find((option) => option.value === status)
                    ?.label ?? status
              )
              .join(', '),
          filterOperators: [
            ...membershipOperators,
            ...emptyOperators<string[]>(),
          ],
          getApplyQuickFilterFn: () => null,
        })
      ),
    {
      ...noAnalytics,
      field: 'missingRequirements',
      headerName: 'Missing Requirements',
      flex: 1,
      minWidth: 280,
      sortable: false,
      type: 'multiSelect',
      valueOptions: requirementFilterOptions(
        rows.flatMap((row) => row.requirementFilterValues)
      ),
      valueGetter: (value, row) =>
        isAutogeneratedRow(row) ? value : row.requirementFilterValues,
      valueFormatter: (value, row) =>
        isAutogeneratedRow(row)
          ? formatRequirementFilterValues(value)
          : formatMissingRequirements(row.missingRequirementGroups),
      filterOperators: [...membershipOperators, ...emptyOperators<string[]>()],
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row)
          ? formatRequirementFilterValues(value)
          : renderMissingRequirementsCell(row),
    },
    ...arrangementTypes.map(
      (type): GridColDef<VolunteerBrowserRowV2> => ({
        aggregable: false,
        chartable: true,
        pivotable: true,
        field: `arrangementAssignment:${type}`,
        headerName: `${type} Assignment`,
        type: 'singleSelect',
        minWidth: 180,
        valueOptions: [
          { value: 'assigned', label: 'Assigned' },
          { value: 'unassigned', label: 'Unassigned' },
        ],
        valueGetter: (_value, row) => row.arrangementAssignmentValues[type],
        getApplyQuickFilterFn: () => null,
        valueFormatter: (value) =>
          value === 'assigned' ? 'Assigned' : 'Unassigned',
      })
    ),
    {
      field: 'volunteerFamilyCount',
      headerName: 'Volunteer family count',
      type: 'number',
      minWidth: 180,
      aggregable: true,
      availableAggregationFunctions: ['sum'],
      chartable: false,
      pivotable: false,
      valueGetter: (_value, row) => row.volunteerFamilyCount,
      getApplyQuickFilterFn: () => null,
    },
    ...buildVolunteerCustomColumns('Volunteer', volunteerCustomFields, rows),
    ...buildVolunteerCustomColumns('Family', familyCustomFields, rows),
    {
      ...noAnalytics,
      field: 'organizationNames',
      headerName: 'Organization',
      flex: 1,
      minWidth: 180,
      sortable: false,
      type: 'multiSelect',
      valueOptions: Array.from(
        new Set(rows.flatMap((row) => row.organizationNames))
      ).sort((first, second) => first.localeCompare(second)),
      valueGetter: (_value, row) => row.organizationNames,
      valueFormatter: (_value, row) => row.organizationNames.join(', '),
      filterOperators: [...membershipOperators, ...emptyOperators<string[]>()],
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          value
        ) : (
          <Typography noWrap {...v2Typography.browserCell}>
            {row.organizationNames.join(', ') || '-'}
          </Typography>
        ),
    },
    {
      ...noAnalytics,
      field: VOLUNTEERS_SEARCH_FIELD,
      headerName: 'Search',
      hideable: false,
      filterable: false,
      sortable: false,
      pinnable: false,
      disableExport: true,
      valueGetter: (_value, row) => row.searchableText,
      getApplyQuickFilterFn: (value) => {
        const term = String(value).toLowerCase();
        return (cellValue) => String(cellValue).toLowerCase().includes(term);
      },
    },
  ];
  return columns.map((column) => ({
    ...column,
    ...(column.valueGetter && {
      valueGetter: (value, row, definition, apiRef) =>
        isAutogeneratedRow(row)
          ? null
          : column.valueGetter!(value, row, definition, apiRef),
    }),
    ...(column.valueFormatter && {
      valueFormatter: (value, row, definition, apiRef) =>
        isAutogeneratedRow(row)
          ? typeof value === 'boolean'
            ? value
              ? 'Yes'
              : 'No'
            : (value ?? '')
          : column.valueFormatter!(value, row, definition, apiRef),
    }),
  }));
}
